import React from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, Package, CheckCircle2, Truck } from 'lucide-react';
import { publicSupplierOrderApi } from '../../lib/api';

interface OrderItem { label: string; qty: number; }

interface OrderInfo {
    supplier_name: string;
    company_name: string;
    template_name: string;
    items: OrderItem[];
    desired_date: string;
    confirmed_at: string | null;
}

const fmtDate = (d?: string | null) => {
    if (!d) return '';
    try { return new Date(d + (d.length <= 10 ? 'T00:00:00' : '')).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }); } catch { return d; }
};

export const SupplierOrderConfirmPage: React.FC = () => {
    const { token } = useParams<{ token: string }>();
    const [info, setInfo] = React.useState<OrderInfo | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState('');
    const [confirming, setConfirming] = React.useState(false);
    const [confirmedAt, setConfirmedAt] = React.useState<string | null>(null);

    React.useEffect(() => {
        if (!token) return;
        publicSupplierOrderApi.getInfo(token).then((d: any) => {
            setInfo(d);
            setConfirmedAt(d?.confirmed_at || null);
        }).catch((e: any) => setError(e?.message || 'Lien invalide ou expiré.')).finally(() => setLoading(false));
    }, [token]);

    const handleConfirm = async () => {
        if (!token) return;
        setConfirming(true);
        setError('');
        try {
            const res: any = await publicSupplierOrderApi.confirm(token);
            setConfirmedAt(res?.confirmed_at || new Date().toISOString());
        } catch (e: any) {
            setError(e?.message || 'Erreur lors de la confirmation.');
        } finally {
            setConfirming(false);
        }
    };

    if (loading) {
        return <div className="min-h-screen flex items-center justify-center bg-surface-app"><Loader2 size={32} className="animate-spin text-content-muted" /></div>;
    }

    if (error && !info) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-surface-app p-4 text-center">
                <p className="text-content font-bold mb-1">Lien invalide</p>
                <p className="text-content-muted text-sm">{error}</p>
            </div>
        );
    }

    if (!info) return null;

    return (
        <div className="min-h-screen bg-surface-app py-10 px-4 flex items-center justify-center">
            <div className="max-w-md w-full bg-surface rounded-2xl border border-border p-8 space-y-6">
                <div className="text-center space-y-1">
                    <div className="w-14 h-14 mx-auto rounded-2xl bg-surface-soft border border-border flex items-center justify-center mb-3">
                        <Truck size={26} className="text-content-muted" />
                    </div>
                    <h1 className="text-lg font-bold text-content">{info.supplier_name}</h1>
                    <p className="text-sm text-content-muted">Commande de {info.company_name}{info.template_name ? ` — ${info.template_name}` : ''}</p>
                </div>

                <div className="bg-surface-soft rounded-xl border border-border p-4 space-y-2">
                    <p className="text-xs font-bold text-content-muted uppercase tracking-widest flex items-center gap-1.5"><Package size={13} /> Articles commandés</p>
                    <ul className="space-y-1">
                        {info.items.map((it, idx) => (
                            <li key={idx} className="text-sm text-content flex items-center justify-between">
                                <span>{it.label}</span>
                                <span className="text-content-muted">x{it.qty}</span>
                            </li>
                        ))}
                    </ul>
                    {info.desired_date && (
                        <p className="text-xs text-content-muted pt-2 border-t border-border">Réception souhaitée : <span className="font-bold text-content">{fmtDate(info.desired_date)}</span></p>
                    )}
                </div>

                {error && <p className="text-xs text-red-500 font-semibold text-center">{error}</p>}

                {confirmedAt ? (
                    <div className="flex flex-col items-center gap-2 text-center py-2">
                        <CheckCircle2 size={32} className="text-green-500" />
                        <p className="text-sm font-bold text-content">Commande confirmée</p>
                        <p className="text-xs text-content-muted">Merci, {info.company_name} a été prévenu que vous avez bien pris en compte cette commande.</p>
                    </div>
                ) : (
                    <button onClick={handleConfirm} disabled={confirming}
                        className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg font-bold text-sm disabled:opacity-50">
                        {confirming ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
                        {confirming ? 'Confirmation...' : 'Confirmer la prise en compte de cette commande'}
                    </button>
                )}
            </div>
        </div>
    );
};
